import { ASSETS_URL } from "@next-platform/client-common-core/infra/constants";
import { HttpStatusCode } from "@next-platform/packages-ts-types";

const DEFAULT_EXTENSION = "webp";
const JPEG_EXTENSION = "jpg";
// Some browsers, Safari among them, start reading the blob after the click returns; revoking at once loses the save.
const OBJECT_URL_REVOKE_DELAY_MS = 60_000;
// How the CDN answers for an object that no longer exists; any other error status may be temporary.
const MISSING_OBJECT_STATUSES: ReadonlySet<number> = new Set([HttpStatusCode.FORBIDDEN, HttpStatusCode.NOT_FOUND]);

/** `<id>.<extension>`, the extension taken from the MIME type the backend stored. */
export const getMediaFileName = (id: string, contentType: string): string => {
  const subtype = contentType.split("/")[1]?.trim().toLowerCase();
  const extension = subtype === "jpeg" ? JPEG_EXTENSION : subtype || DEFAULT_EXTENSION;
  return `${id}.${extension}`;
};

/**
 * The API returns the storage origin URL, whose cached responses carry no CORS headers, so a browser cannot
 * read its bytes. The image CDN pulls from that origin under the same keys and always allows cross-origin
 * reads, so the download goes through it.
 */
export const toDownloadUrl = (url: string): string => {
  if (!ASSETS_URL || url.startsWith(`${ASSETS_URL}/`)) return url;
  const { pathname, search } = new URL(url);
  return `${ASSETS_URL}${pathname}${search}`;
};

/** `saved` covers the fallback tab too: the browser can still save the image from there. */
export type MediaFileDownloadOutcome = "saved" | "unavailable" | "failed";

/**
 * Saves a media file to disk. The CDN is another origin, so a plain `download` link would only navigate
 * to the image; the bytes are fetched into a blob first and saved under the given name. Should the CDN
 * refuse the cross-origin read, the image opens in a new tab, where the browser can still save it.
 *
 * Resolves `unavailable` when the CDN answers 403 or 404, as it does for an object that no longer exists:
 * opening the URL would only show the user the storage service's raw error page. Resolves
 * `failed` when nothing reached the user, such as a popup blocker swallowing the fallback tab.
 */
export default async function downloadMediaFile({ url, fileName }: { url: string; fileName: string }): Promise<MediaFileDownloadOutcome> {
  try {
    const response = await fetch(toDownloadUrl(url), { mode: "cors" });
    if (MISSING_OBJECT_STATUSES.has(response.status)) {
      return "unavailable";
    }
    if (!response.ok) {
      throw new Error(`Download failed with status ${response.status}`);
    }
    const objectUrl = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_REVOKE_DELAY_MS);
    return "saved";
  } catch {
    // Opened without `noopener`, which would make `window.open` return `null` even for a tab that did open.
    const fallbackWindow = window.open(url, "_blank");
    if (fallbackWindow === null) {
      return "failed";
    }
    fallbackWindow.opener = null;
    return "saved";
  }
}
